import { c as _c } from "react/compiler-runtime";
import type { ResearchFile } from '@shared/all/domains/socket';
import { researchFileName } from '@shared/all/helpers/researchFile';
import { useState } from 'react';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

// The file card under an Ask answer (a file the assistant made with code
// execution), minus how it is drawn: the four cards — Oakbox web and app,
// factiii.com web and app — each render it their own way, and each opens a
// download URL their own way, but what the card knows and does is this.
//
// A failed call needs nothing here: every client's tRPC link toasts the
// server's message (or shows the offline banner), so the card only resets.

type ResearchFileChoice = 'replace' | 'keep_both' | 'discard';

/** The answers to "a file is already there", in the order cards show them. */
export const RESEARCH_FILE_CHOICES: {
  choice: ResearchFileChoice;
  label: string;
  bad?: boolean;
}[] = [{
  choice: 'replace',
  label: 'Replace',
  bad: true
}, {
  choice: 'keep_both',
  label: 'Keep both'
}, {
  choice: 'discard',
  label: "Don't save"
}];
const useResearchFile = (client, initial, sessionId, onFilesChanged) => {
  const $ = _c(17);
  const [file, setFile] = useState(initial);
  const [busy, setBusy] = useState(null);
  let t0;
  if ($[0] !== client || $[1] !== file.uploadId) {
    t0 = async () => {
      if (!file.uploadId) {
        return null;
      }
      setBusy("download");
      const [err, res] = await client.onedriveEmbed.downloadUrl.mutate({
        uploadId: file.uploadId,
        download: true
      });
      setBusy(null);
      return err ? null : res.url;
    };
    $[0] = client;
    $[1] = file.uploadId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const downloadUrl = t0;
  let t1;
  if ($[3] !== client || $[4] !== file.id || $[5] !== onFilesChanged || $[6] !== sessionId) {
    t1 = async choice => {
      if (!sessionId) {
        return;
      }
      setBusy(choice);
      const [err_0, res_0] = await client.research.resolveFile.mutate({
        sessionId,
        fileId: file.id,
        choice
      });
      setBusy(null);
      if (err_0) {
        return;
      }
      setFile(res_0);
      onFilesChanged?.();
    };
    $[3] = client;
    $[4] = file.id;
    $[5] = onFilesChanged;
    $[6] = sessionId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const resolve = t1;
  let t2;
  if ($[8] !== file.path) {
    t2 = researchFileName(file.path);
    $[8] = file.path;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = file.status === "saved" && file.uploadId !== null;
  let t4;
  if ($[10] !== busy || $[11] !== downloadUrl || $[12] !== file || $[13] !== resolve || $[14] !== t2 || $[15] !== t3) {
    t4 = {
      file,
      busy,
      name: t2,
      saved: t3,
      downloadUrl,
      resolve
    };
    $[10] = busy;
    $[11] = downloadUrl;
    $[12] = file;
    $[13] = resolve;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
export default useResearchFile;